import {
  Component,
  DestroyRef,
  ElementRef,
  HostListener,
  OnInit,
  ViewEncapsulation,
  computed,
  effect,
  inject,
  signal,
  untracked,
  ChangeDetectionStrategy
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { HttpClient } from '@angular/common/http';
import {
  type Comment,
  CommentService,
  type UnreadCommentCounts,
  Feedback,
  FeedbackApiTokenStore,
  type FeedbackRoundPage,
  FeedbackRoundService,
  FeedbackService,
  FeedbackStatus,
  FeedbackStub,
  FeedbackType,
  FilesService,
  PageStatus,
  type Paginated,
  type ScreenType,
  type ValidateRoundResponse,
} from '@brafton/feedback-api';
import { firstValueFrom, forkJoin, map, of, switchMap, type Observable } from 'rxjs';
import { detect } from 'detect-browser';

import { environment } from '../environments/environment';
import { requiresEmbedAllowedCheck } from './widget-activation';
import { WIDGET_VERSION } from './widget-version';
import { AuthBridgeService } from './services/auth-bridge.service';
import { ReporterIdentity } from './dialogs/identity-modal/identity-modal';
import { NewFeedbackDraft } from './dialogs/new-feedback/new-feedback';
import { FeedbackStatusChange } from './components/feedback-list/feedback-list';
import { AnchorRect } from './dialogs/feedback-detail/feedback-detail';
import type { CommentThreadEntry, DisregardReason, FeedbackAssetDraft, NewComment } from '@brafton/feedback-components';
import { disregardReasonComment } from '@brafton/feedback-components';
import { MarkerPositioner } from './markers/marker-positioner';
import { MarkerLayer } from './markers/marker-layer';
import { applyMarkerUnreadBadge } from './markers/marker-unread-badge';
import { ScreenshotCapture } from './screenshotCapture';

type StatusKind = 'success' | 'error';

/**
 * Root of the embeddable feedback widget. Hosts the floating "Give feedback"
 * launcher and the slide-out feedback panel.
 *
 * On init it reads the `org` and `project` attributes from the host
 * <brafton-feedback> element, validates the feedback round against the API, and
 * (on success) stores the returned JWT for the feedback-api auth interceptor.
 * The validation outcome is shown in the slide-out panel.
 */
@Component({
  selector: 'brafton-feedback',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss', './app.component.premium.scss', './app.component.panel.premium.scss'],
  standalone: false,
  // Shadow DOM isolates the widget UI from the host page's global CSS (which
  // otherwise bleeds in — e.g. theme `button { padding }` breaking the header).
  encapsulation: ViewEncapsulation.ShadowDom,
  // Hide the whole widget for the instant the screenshot frame is grabbed (signal
  // read, so it recalculates when `capturing` changes).
  changeDetection: ChangeDetectionStrategy.Eager,
  host: { '[class.is-capturing]': 'capturing()' },
})
export class AppComponent implements OnInit {
  private readonly host = inject(ElementRef);
  private readonly feedbackRounds = inject(FeedbackRoundService);
  private readonly tokenStore = inject(FeedbackApiTokenStore);
  private readonly authBridge = inject(AuthBridgeService);
  private readonly screenshotCapture = inject(ScreenshotCapture);
  private readonly feedback = inject(FeedbackService);
  private readonly comments = inject(CommentService);
  private readonly files = inject(FilesService);
  private readonly http = inject(HttpClient);
  private readonly destroyRef = inject(DestroyRef);

  /**
   * Whether native screen capture is available. When false the capture modal hides
   * the Video/Delay options and offers manual screenshot upload instead.
   */
  public readonly nativeCaptureAvailable = this.screenshotCapture.nativeCaptureAvailable;

  /** The validated feedback round id (from the round validation on init). */
  protected feedbackRoundId: string | null = null;

  /** Markers currently rendered on the host page for existing feedback. */
  private renderedMarkers: HTMLElement[] = [];

  /** Feedback id → its rendered marker element, for opening the detail beside it. */
  private readonly markerElements = new Map<string, HTMLElement>();

  /** The feedback whose detail popover is open (null = closed). */
  public readonly selectedFeedback = signal<Feedback | null>(null);

  /** The marker rectangle the detail popover positions against. */
  public readonly detailAnchor = signal<AnchorRect | null>(null);

  /** The marker currently highlighted/revealed for the open detail popover. */
  private selectedMarker: HTMLElement | null = null;

  /** The mockup assigned to the current page (null when the page has none). */
  public readonly currentMockupUrl = signal<string | null>(null);

  /** All mockups uploaded for this round — the pool to pick from when unassigned. */
  public readonly availableMockups = signal<string[]>([]);

  /** Whether a mockup is currently being assigned to this page. */
  public readonly assigningMockup = signal(false);

  /** Whether the mockup lightbox is open. */
  public readonly mockupOpen = signal(false);

  /** Page label shown in the mockup lightbox subtitle. */
  public readonly mockupPageLabel = window.location.pathname;

  /** localStorage key for the show/hide-markers preference (per origin/domain). */
  private static readonly MARKERS_HIDDEN_STORAGE_KEY = 'fb_markers_hidden_v1';

  /** Reads the stored markers-hidden preference; hidden by default. */
  private static readMarkersHidden(): boolean {
    try {
      // Hidden unless the user previously chose to show them ('0').
      return localStorage.getItem(AppComponent.MARKERS_HIDDEN_STORAGE_KEY) !== '0';
    } catch {
      return true;
    }
  }

  /** Whether on-page markers are hidden. Restored from localStorage; hidden by default. */
  public readonly markersHidden = signal(AppComponent.readMarkersHidden());

  /** Debounce timer for re-positioning markers on viewport resize. */
  private resizeTimer: ReturnType<typeof setTimeout> | null = null;

  constructor() {
    // Re-render the on-page markers whenever the (filtered) feedback changes —
    // initial load, a status/screen filter change, or after a new submission.
    effect(() => this.renderFeedbackMarkers(this.filteredFeedback()));
    // While a screenshot frame is grabbed, hide existing markers so only the
    // just-dropped (active) one lands in the shot.
    effect(() => document.body.classList.toggle('brafton-capturing', this.capturing()));
    // Markers start hidden; the header eye control toggles `brafton-pins-hidden`.
    effect(() => document.body.classList.toggle('brafton-pins-hidden', this.markersHidden()));
    // Keep the open detail popover beside its marker while the page scrolls. Capture
    // phase, so pages that scroll an inner container rather than the window are covered.
    const followOnScroll = () => this.scheduleDetailAnchorUpdate();
    document.addEventListener('scroll', followOnScroll, { capture: true, passive: true });
    this.destroyRef.onDestroy(() => {
      this.clearRenderedMarkers();
      if (this.resizeTimer !== null) {
        clearTimeout(this.resizeTimer);
      }
      if (this.detailAnchorFrame !== null) {
        cancelAnimationFrame(this.detailAnchorFrame);
      }
      document.removeEventListener('scroll', followOnScroll, { capture: true });
      document.body.classList.remove('brafton-pins-hidden', 'brafton-capturing');
    });
  }

  /** Pending animation frame for re-measuring the open detail's marker. */
  private detailAnchorFrame: number | null = null;

  /**
   * Re-measures the open detail's marker (at most once per frame) so the popover,
   * which is fixed to the viewport, moves with the marker as the page scrolls.
   */
  private scheduleDetailAnchorUpdate(): void {
    if (this.detailAnchorFrame !== null || !this.selectedFeedback() || !this.selectedMarker) {
      return;
    }
    this.detailAnchorFrame = requestAnimationFrame(() => {
      this.detailAnchorFrame = null;
      const marker = this.selectedMarker;
      if (!this.selectedFeedback() || !marker) {
        return;
      }
      const markerRect = marker.getBoundingClientRect();
      this.detailAnchor.set({
        left: markerRect.left,
        top: markerRect.top,
        width: markerRect.width,
        height: markerRect.height,
      });
    });
  }

  /** Toggles on-page marker visibility (the header eye control) and persists it. */
  toggleMarkers(): void {
    const hidden = !this.markersHidden();
    this.markersHidden.set(hidden);
    try {
      localStorage.setItem(AppComponent.MARKERS_HIDDEN_STORAGE_KEY, hidden ? '1' : '0');
    } catch {
      // Storage unavailable (private mode / blocked) — non-fatal.
    }
  }

  /** Opens the assigned-mockup lightbox for the current page. */
  openMockup(): void {
    this.mockupOpen.set(true);
  }

  /** Closes the mockup lightbox. */
  closeMockup(): void {
    this.mockupOpen.set(false);
  }

  /**
   * Assigns a mockup (chosen from the round's pool) to the current page, then
   * shows it in the lightbox. Used when the page has no mockup assigned yet.
   */
  onMockupSelect(mockupUrl: string): void {
    const roundId = this.feedbackRoundId;
    if (!roundId || !mockupUrl) {
      return;
    }
    this.assigningMockup.set(true);
    this.feedbackRounds
      .assignMockupPages(roundId, { mockupUrl, pageUrl: window.location.href })
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: () => {
          this.currentMockupUrl.set(mockupUrl);
          this.assigningMockup.set(false);
        },
        error: () => this.assigningMockup.set(false),
      });
  }

  /**
   * Resolves the current page and the round's mockup pool. The page comes from
   * `ensurePage`, which returns this URL's page on the round, adding it (Pending,
   * no mockup) first if it isn't tracked yet. The widget never downloads the
   * round's page list, which can run to tens of thousands of pages.
   */
  private loadCurrentPage(roundId: string): void {
    this.feedbackRounds
      .getFeedbackRound(roundId)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (round) => this.availableMockups.set(round.mockupUrls ?? []),
        // A failed round fetch just leaves the mockup pool empty — non-critical.
      });
    this.feedbackRounds
      .ensurePage(roundId, window.location.href)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (ensured) => this.applyCurrentPage(ensured.page),
        // A failed lookup just leaves the mockup/status unavailable — non-critical.
      });
  }

  /** Adopts a resolved round page's mockup + status into the header. */
  private applyCurrentPage(page: FeedbackRoundPage): void {
    this.currentMockupUrl.set(page.mockupUrl ?? page.mockupUrls?.[0] ?? null);
    if (page.status) {
      this.currentPageStatus.set(page.status);
    }
  }

  /** Re-fetches the round-wide feedback total for the "Pins" stat. */
  private loadRoundFeedbackTotal(roundId: string): void {
    this.feedback
      .listFeedback({ feedbackRoundId: roundId, page: 1 })
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (result) => this.roundFeedbackTotal.set(result.total),
      });
  }

  /** Persists a new page status when the header status chip changes. */
  onPageStatusChange(newStatus: string): void {
    const roundId = this.feedbackRoundId;
    if (!roundId) {
      return;
    }
    const previousStatus = this.currentPageStatus();
    this.currentPageStatus.set(newStatus);
    this.feedbackRounds
      .updatePageStatus(roundId, newStatus as PageStatus, window.location.href)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        // Roll back the optimistic chip change if the update fails.
        error: () => this.currentPageStatus.set(previousStatus),
      });
  }

  /** Re-positions markers for the new viewport when the window resizes (debounced). */
  @HostListener('window:resize')
  onViewportResize(): void {
    if (this.resizeTimer !== null) {
      clearTimeout(this.resizeTimer);
    }
    this.resizeTimer = setTimeout(() => {
      this.resizeTimer = null;
      this.renderFeedbackMarkers(this.filteredFeedback());
      // The open detail's marker was just re-created in its new spot; follow it.
      this.scheduleDetailAnchorUpdate();
    }, 150);
  }

  /** Feedback app origin — Sign in destination + auth-bridge host. */
  public readonly appBaseUrl = environment.appBaseUrl;

  /** The widget's build version, shown in the panel footer. */
  public readonly widgetVersion = WIDGET_VERSION;

  /** Whether the host browser is a logged-in Brafton user (via the auth bridge). */
  public readonly loggedIn = signal(false);

  /** The validate JWT used for the auth-bridge probe; kept so we can re-probe. */
  private bridgeJwt: string | null = null;

  /** Pending "re-probe the bridge after a Sign-in click" timer (one at a time). */
  private signInRetryTimer: ReturnType<typeof setTimeout> | null = null;

  /** How long after a Sign-in click to re-probe the bridge for the new session. */
  private readonly signInRetryMs = 30_000;

  /** Whether the slide-out feedback panel is open. */
  public readonly panelOpen = signal(false);

  /** Page-status options for the header status chip (from the API enum). */
  public readonly pageStatuses: string[] = Object.values(PageStatus);

  /** The current page's review status (from the round's page list). */
  public readonly currentPageStatus = signal<string>(PageStatus.Pending);

  /** Total feedback items in the whole round (the "Pins" stat). */
  public readonly roundFeedbackTotal = signal(0);

  /**
   * Whether AI-generated feedback is shown. Off by default: the widget is the client's
   * view of the page, and AI findings are review material rather than something they
   * asked to see. The toolbar toggle opts in.
   */
  public readonly showAiFeedback = signal(false);

  /**
   * The feedback this visitor is allowed to see, before the status/viewport filters.
   * Every visible count and list derives from this, so the AI toggle governs the whole
   * widget consistently.
   */
  private readonly visibleFeedback = computed<Feedback[]>(() =>
    this.showAiFeedback()
      ? this.allFeedback()
      : this.allFeedback().filter((item) => item.type !== FeedbackType.AiFeedback),
  );

  /** How many AI items the toggle would reveal; drives the toolbar count. */
  public readonly aiFeedbackCount = computed(
    () => this.allFeedback().filter((item) => item.type === FeedbackType.AiFeedback).length,
  );

  /** Feedback on the current page (the "Viewing" stat). */
  public readonly viewingCount = computed(() => this.visibleFeedback().length);

  /** Feedback on the current page that's been resolved (completed/disregarded). */
  public readonly resolvedCount = computed(
    () =>
      this.visibleFeedback().filter(
        (item) => item.status === FeedbackStatus.Completed || item.status === FeedbackStatus.Disregard,
      ).length,
  );

  /** Feedback on the current page still open (total less resolved). */
  public readonly openCount = computed(() => this.visibleFeedback().length - this.resolvedCount());

  /** Status options for the toolbar's multi-select filter (FeedbackStatus values). */
  public readonly statusFilterOptions: ReadonlyArray<{ value: FeedbackStatus; label: string }> = [
    { value: FeedbackStatus.Action, label: 'Action' },
    { value: FeedbackStatus.NeedsInternalReview, label: 'Needs Internal Review' },
    { value: FeedbackStatus.NeedsClientReview, label: 'Needs Client Review' },
    { value: FeedbackStatus.Revise, label: 'Revise' },
    { value: FeedbackStatus.RequestForAdditionalInformation, label: 'Req. for More Info' },
    { value: FeedbackStatus.Completed, label: 'Completed' },
    { value: FeedbackStatus.Disregard, label: 'Disregard' },
  ];

  /** Selected statuses (empty = all). Multi-select. */
  public readonly statusFilter = signal<Set<FeedbackStatus>>(new Set());

  /** Selected screen filter (null = all). Single-select; nothing highlighted by default. */
  public readonly screenFilter = signal<string | null>(null);

  /** Whether the status-filter menu is open. */
  public readonly filterMenuOpen = signal(false);

  /** Trigger label: "All status", the single selected status, or "Status" for several. */
  public readonly statusFilterLabel = computed(() => {
    const selected = this.statusFilter();
    if (selected.size === 0) {
      return 'All status';
    }
    if (selected.size === 1) {
      const [only] = [...selected];
      return this.statusFilterOptions.find((option) => option.value === only)?.label ?? 'Status';
    }
    return 'Status';
  });

  /** Count of feedback items per status, for the menu badges. */
  public readonly statusCounts = computed<Record<string, number>>(() => {
    const counts: Record<string, number> = {};
    for (const item of this.visibleFeedback()) {
      counts[item.status] = (counts[item.status] ?? 0) + 1;
    }
    return counts;
  });

  toggleFilterMenu(): void {
    this.filterMenuOpen.update((open) => !open);
  }

  isStatusSelected(value: FeedbackStatus): boolean {
    return this.statusFilter().has(value);
  }

  /** Toggles a status in the multi-select filter and resets to the first page. */
  toggleStatus(value: FeedbackStatus): void {
    const next = new Set(this.statusFilter());
    if (next.has(value)) {
      next.delete(value);
    } else {
      next.add(value);
    }
    this.statusFilter.set(next);
    this.feedbackPage.set(1);
  }

  selectAllStatuses(): void {
    this.statusFilter.set(new Set(this.statusFilterOptions.map((option) => option.value)));
    this.feedbackPage.set(1);
  }

  clearStatuses(): void {
    this.statusFilter.set(new Set());
    this.feedbackPage.set(1);
  }

  /** Single-select screen filter; clicking the active device clears it (none by default). */
  /**
   * Shows or hides AI-generated feedback. Filtering is client-side (the round's items
   * are already loaded), so unlike the viewport filter this needs no refetch — but the
   * pager has to go back to page 1, since the result count changes underneath it.
   */
  toggleAiFeedback(): void {
    this.showAiFeedback.update((shown) => !shown);
    this.feedbackPage.set(1);
  }

  /** Label for the collapsed viewport control, which shows the current choice. */
  public readonly screenFilterTitle = computed(() => {
    const screen = this.screenFilter();
    if (!screen) {
      return 'All sizes';
    }
    return screen.charAt(0).toUpperCase() + screen.slice(1);
  });

  /** Clears the viewport filter (the reveal's "All sizes" option). */
  clearScreenFilter(): void {
    if (this.screenFilter() === null) {
      return;
    }
    this.screenFilter.set(null);
    this.feedbackPage.set(1);
    // The screen filter is applied server-side, so dropping it needs a re-query.
    this.loadAllFeedback();
  }

  toggleScreen(device: string): void {
    this.screenFilter.update((current) => (current === device ? null : device));
    this.feedbackPage.set(1);
    // Re-query with the new screen filter (it's applied server-side).
    this.loadAllFeedback();
  }

  // ===========================================================================
  // Add-feedback flow: identity → pin placement → capture-timing modal.
  // The modals/overlay are their own components (dialogs/, components/); this
  // host owns the orchestration: identity storage, placement lifecycle, and
  // marker injection. (The actual screenshot capture is wired up later.)
  // ===========================================================================

  /** localStorage key for the stored reporter identity (name + email). */
  private static readonly IDENTITY_STORAGE_KEY = 'fb_identity_v1';

  /** Whether the identity-collection modal is open. */
  public readonly identityModalOpen = signal(false);

  /** Whether the user is currently placing a feedback marker on the page. */
  public readonly placing = signal(false);

  /** Whether the capture-timing modal is open. */
  public readonly captureModalOpen = signal(false);

  /** The marker number shown in the capture modal copy (from the feedback stub). */
  public readonly captureMarkerNumber = signal(0);

  /** The server-created feedback stub for the in-progress feedback (finalized on submit). */
  private pendingStub: FeedbackStub | null = null;

  /** The most-recently dropped marker, kept so it can be removed if cancelled. */
  private pendingMarker: HTMLElement | null = null;

  /** Viewport coordinates of the dropped marker, used to place the draft popover. */
  public readonly markerAnchorX = signal(0);
  public readonly markerAnchorY = signal(0);

  /** The dropped marker's recorded data, captured at placement time. */
  private markerPageX = 0; // absolute X relative to the page (body), in px
  private markerPageY = 0; // absolute Y relative to the page (body), in px
  private markerScreenWidth = 0; // viewport width when placed
  private markerScreenHeight = 0; // viewport height when placed
  private markerGrid = 0; // 1-based cell in the 6×24 page grid
  private markerXPath = ''; // XPath of the element directly under the marker

  /** True while uploading the screenshot/video + creating the feedback. */
  public readonly submitting = signal(false);

  /** All feedback for the current page URL (fetched once; filtered + paged client-side). */
  private readonly allFeedback = signal<Feedback[]>([]);

  /**
   * Rows shown per page in the panel list. Capped at 15 for the narrow widget panel
   * (independent of the larger page size used to bulk-fetch all feedback below).
   */
  public readonly feedbackPerPage = 15;

  /**
   * Page size used when bulk-loading ALL feedback for the page (max the API allows),
   * so the all-pages fetch is one or two requests instead of dozens.
   */
  private readonly bulkFetchPerPage = 200;

  /** Current (client-side) page of the list. */
  public readonly feedbackPage = signal(1);

  /** Feedback after the active status + screen filters are applied. */
  private readonly filteredFeedback = computed<Feedback[]>(() => {
    const statuses = this.statusFilter();
    const screen = this.screenFilter();
    return this.visibleFeedback().filter((item) => {
      const statusOk = statuses.size === 0 || statuses.has(item.status);
      const itemScreen =
        typeof item.meta?.['screen'] === 'string' ? (item.meta['screen'] as string) : null;
      const screenOk = !screen || itemScreen === screen;
      return statusOk && screenOk;
    });
  });

  public readonly feedbackTotal = computed(() => this.filteredFeedback().length);

  public readonly feedbackLastPage = computed(() =>
    Math.max(1, Math.ceil(this.feedbackTotal() / this.feedbackPerPage)),
  );

  /** The current page's slice of the filtered feedback. */
  public readonly feedbackItems = computed<Feedback[]>(() => {
    const page = Math.min(this.feedbackPage(), this.feedbackLastPage());
    const start = (page - 1) * this.feedbackPerPage;
    return this.filteredFeedback().slice(start, start + this.feedbackPerPage);
  });

  /**
   * Loads ALL feedback for the current page URL (newest first), paging through the
   * server's pagination, so the list can filter + paginate client-side.
   */
  private loadAllFeedback(): void {
    const roundId = this.feedbackRoundId;
    if (!roundId) {
      return;
    }
    this.fetchFeedbackPage(roundId, 1)
      .pipe(
        switchMap((first) => {
          if (first.lastPage <= 1) {
            return of(first.results);
          }
          const remaining: Observable<Paginated<Feedback>>[] = [];
          for (let page = 2; page <= first.lastPage; page++) {
            remaining.push(this.fetchFeedbackPage(roundId, page));
          }
          return forkJoin(remaining).pipe(
            map((pages) => [...first.results, ...pages.flatMap((pageResult) => pageResult.results)]),
          );
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe((items) => {
        this.allFeedback.set(items);
        // Feedback for this page has landed, so the unread counts can be fetched for it.
        this.loadUnreadCounts();
      });
  }

  private fetchFeedbackPage(roundId: string, page: number): Observable<Paginated<Feedback>> {
    return this.feedback.listFeedback({
      feedbackRoundId: roundId,
      url: window.location.href,
      page,
      // Pull large pages so loading all feedback is 1–2 requests, not dozens.
      perPage: this.bulkFetchPerPage,
      sort: 'created:desc',
      // Apply the current screen filter server-side (single-value, backend-supported).
      // The multi-select status filter stays client-side (the API takes one status,
      // and server-filtering it would skew the per-status menu counts).
      screen: (this.screenFilter() ?? undefined) as ScreenType | undefined,
    });
  }

  /** Pages through the (client-side) list. */
  onFeedbackPageChange(page: number): void {
    this.feedbackPage.set(page);
  }

  /**
   * Persists a status change made on a row's chip. Optimistically updates the
   * local list (so it re-filters/re-renders immediately) and reverts on failure.
   */
  onFeedbackStatusChange(statusChange: FeedbackStatusChange, disregardReason?: DisregardReason): void {
    const status = statusChange.status as FeedbackStatus;
    // Disregarding needs a reason first: ask, and carry on from onDisregardConfirmed.
    if (status === FeedbackStatus.Disregard && !disregardReason) {
      const pendingItem = this.allFeedback().find((feedbackItem) => feedbackItem.id === statusChange.id);
      this.disregardPending.set({ statusChange, element: pendingItem?.element ?? null });
      return;
    }
    const previousFeedbackList = this.allFeedback();
    const previouslySelectedFeedback = this.selectedFeedback();
    this.allFeedback.set(
      previousFeedbackList.map((feedbackItem) => (feedbackItem.id === statusChange.id ? { ...feedbackItem, status } : feedbackItem)),
    );
    // Keep the open detail popover's chip in sync with the change.
    if (previouslySelectedFeedback && previouslySelectedFeedback.id === statusChange.id) {
      this.selectedFeedback.set({ ...previouslySelectedFeedback, status });
    }
    this.feedback
      // The reporter's saved identity is recorded as who made the statusChange.
      .updateFeedbackStatus(statusChange.id, status, this.readIdentity() ?? undefined)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: () => {
          if (disregardReason) {
            this.recordDisregardReason(statusChange.id, disregardReason);
          }
        },
        error: () => {
          this.allFeedback.set(previousFeedbackList);
          if (previouslySelectedFeedback && previouslySelectedFeedback.id === statusChange.id) {
            this.selectedFeedback.set(previouslySelectedFeedback);
          }
        },
      });
  }

  /** An item waiting on a reason before it's disregarded (the reason dialog is open). */
  readonly disregardPending = signal<{ statusChange: FeedbackStatusChange; element: string | null } | null>(null);

  onDisregardConfirmed(disregardReason: DisregardReason): void {
    const pendingDisregard = this.disregardPending();
    this.disregardPending.set(null);
    if (pendingDisregard) {
      this.onFeedbackStatusChange(pendingDisregard.statusChange, disregardReason);
    }
  }

  /** Backing out leaves the status as it was (it was never changed). */
  onDisregardCancelled(): void {
    this.disregardPending.set(null);
  }

  /**
   * Saves why an item was disregarded as a comment, emailing nobody. It's public:
   * the widget's token can't post private comments (the main app's reason is
   * private). The audit report reads these comments.
   */
  private recordDisregardReason(feedbackId: string, disregardReason: DisregardReason): void {
    const identity = this.readIdentity();
    if (!identity) {
      return;
    }
    this.comments
      .createComment(feedbackId, {
        comment: disregardReasonComment(disregardReason),
        createdBy: { name: identity.name, email: identity.email },
        notify: false,
      })
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: () => {
          if (this.selectedFeedback()?.id === feedbackId) {
            this.loadComments(feedbackId);
          }
        },
        error: () => this.setStatus('error', 'The item was disregarded, but the reason could not be saved.'),
      });
  }

  /**
   * Renders a marker on the host page for each feedback item, positioned by the
   * MarkerPositioner (which scales / grid-snaps / skips based on how the capture
   * screen compares to the current viewport). Clears the previous markers first.
   */
  private renderFeedbackMarkers(items: Feedback[]): void {
    this.clearRenderedMarkers();
    const viewport = MarkerPositioner.currentViewport();
    // Read the open selection without subscribing, so this doesn't re-run when the
    // detail opens/closes (only when the feedback set itself changes).
    const selectedId = untracked(() => this.selectedFeedback())?.id ?? null;
    for (const item of items) {
      if (!item.marker) {
        continue;
      }
      const point = MarkerPositioner.resolve(item.marker, viewport);
      if (!point) {
        continue;
      }
      const marker = this.createFeedbackMarker(item);
      MarkerLayer.place(marker, point.x, point.y);
      this.renderedMarkers.push(marker);
      this.markerElements.set(item.id, marker);
      // Keep the open detail's marker revealed/highlighted (and pulsing) across re-renders.
      if (item.id === selectedId) {
        marker.classList.add('is-active', 'is-pulsing');
        this.selectedMarker = marker;
      }
    }

    // Markers were just (re)created, so paint the badges we already have. This runs
    // inside the render effect, so the read MUST be untracked: tracking unreadCounts
    // here (and especially fetching it) turns each render into another render.
    untracked(() => this.syncMarkerUnreadBadges());
  }

  /** Removes all currently-rendered feedback markers from the page. */
  private clearRenderedMarkers(): void {
    for (const marker of this.renderedMarkers) {
      marker.remove();
    }
    this.renderedMarkers = [];
    this.markerElements.clear();
  }

  /**
   * Builds a clickable marker element for a feedback item. Positioning is left to
   * MarkerLayer, which accounts for where the host page's body actually starts.
   */
  private createFeedbackMarker(item: Feedback): HTMLElement {
    const markerData = (item.marker ?? {}) as Record<string, unknown>;
    const number = Number(markerData['id']) || 0;
    const marker = document.createElement('div');
    marker.className = 'brafton-marker';
    marker.textContent = String(number);
    marker.setAttribute('role', 'button');
    marker.setAttribute('tabindex', '0');
    marker.setAttribute('aria-label', `Feedback ${number}`);
    marker.addEventListener('click', () => this.onMarkerClick(item));
    marker.addEventListener('keydown', (event) => {
      if (event.key === 'Enter' || event.key === ' ') {
        event.preventDefault();
        this.onMarkerClick(item);
      }
    });
    return marker;
  }

  /** Opens the feedback detail when a marker on the page is clicked. */
  onMarkerClick(feedback: Feedback): void {
    this.openFeedbackDetail(feedback);
  }

  /** Opens the feedback detail when a row in the list is clicked. */
  onFeedbackOpen(feedback: Feedback): void {
    this.openFeedbackDetail(feedback);
  }

  /**
   * Opens the detail popover. When the feedback has a placeable marker, the marker
   * is revealed (even when markers are hidden) and highlighted, scrolled into view,
   * then measured to anchor the popover beside it. When there's no marker (no
   * marker data, or it was skipped for this viewport), the popover opens centred on
   * screen (a null anchor → the component centres it).
   */
  private openFeedbackDetail(feedback: Feedback): void {
    if (this.selectedMarker) {
      this.selectedMarker.classList.remove('is-active', 'is-pulsing');
    }
    const marker = this.markerElements.get(feedback.id) ?? null;
    this.selectedMarker = marker;
    if (marker) {
      // `is-active` reveals the marker even when pins are hidden and highlights it;
      // `is-pulsing` makes its ring pulse while the detail popover is open.
      marker.classList.add('is-active', 'is-pulsing');
      marker.scrollIntoView({ block: 'nearest', inline: 'nearest' });
    }
    // Measure after the marker is revealed/scrolled, then open the popover. A
    // missing or empty rect (no marker) → null anchor → the popover centres.
    requestAnimationFrame(() => {
      const rect = marker?.getBoundingClientRect();
      const hasMarker = !!rect && (rect.width > 0 || rect.height > 0);
      this.detailAnchor.set(
        hasMarker ? { left: rect.left, top: rect.top, width: rect.width, height: rect.height } : null,
      );
      this.selectedFeedback.set(feedback);
      this.commentThread.set([]);
      if (feedback.id) {
        this.loadComments(feedback.id);
        // Opening the thread is what clears its badge.
        this.markCommentsRead(feedback.id);
      }
    });
  }

  /**
   * Unread comment counts for this reporter, keyed by feedback id. Empty when no
   * identity is stored — without an address there is no "unread" to speak of.
   *
   * The API only counts comments on feedback this address is part of the conversation
   * on, and never counts private ones for a round-scoped caller like the widget.
   */
  public readonly unreadCounts = signal<UnreadCommentCounts>({});

  /**
   * Repaints the on-page marker badges whenever the counts change.
   *
   * Safe to track here because this effect only READS the counts. Nothing it calls
   * writes them — the fetch lives on the feedback-load path, not in a reactive context.
   */
  private readonly unreadMarkerSync = effect(() => {
    this.unreadCounts();
    this.syncMarkerUnreadBadges();
  });

  /** Refetches the unread counts for the stored identity. */
  private loadUnreadCounts(): void {
    const identity = this.readIdentity();
    const roundId = this.feedbackRoundId;
    if (!identity || !roundId) {
      this.unreadCounts.set({});
      return;
    }

    this.comments
      .listUnreadCounts({ feedbackRoundId: roundId, email: identity.email })
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (counts) => this.unreadCounts.set(counts),
        // Badges are a nicety; leave them as they were.
        error: () => undefined,
      });
  }

  /**
   * Marks a thread read for the stored identity, so the badge clears.
   *
   * A visitor with no stored identity has nothing to mark — they have not been
   * identified, so no unread state exists for them.
   */
  private markCommentsRead(feedbackId: string): void {
    const identity = this.readIdentity();
    if (!identity) {
      return;
    }

    this.comments
      .markCommentsRead(feedbackId, { email: identity.email })
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: () => this.loadUnreadCounts(),
        error: () => undefined,
      });
  }

  /**
   * Paints the unread badges onto the on-page markers.
   *
   * Markers are plain DOM appended to the host page (not part of this component's
   * template), so they are updated imperatively whenever the counts change.
   */
  private syncMarkerUnreadBadges(): void {
    const counts = this.unreadCounts();
    for (const [feedbackId, marker] of this.markerElements) {
      applyMarkerUnreadBadge(marker, counts[feedbackId] ?? 0);
    }
  }

  /** The comment thread for the open popover. Public comments only — see below. */
  public readonly commentThread = signal<CommentThreadEntry[]>([]);

  /** True while the thread is loading. */
  public readonly commentsLoading = signal(false);

  /** True while a comment is being sent. */
  public readonly commentPosting = signal(false);

  /**
   * A comment typed before we knew who the reporter was. The identity modal is opened
   * to collect a name and email, and this is sent once they save.
   */
  private pendingComment: NewComment | null = null;

  /**
   * Loads the thread for a feedback item.
   *
   * The API decides what this returns: the widget holds a round-scoped token, so
   * private (internal) comments are filtered out server-side and never reach the page.
   */
  private loadComments(feedbackId: string): void {
    this.commentsLoading.set(true);
    this.comments
      .listComments(feedbackId)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (comments) => {
          this.commentThread.set(comments.map((comment) => this.toCommentThreadEntry(comment)));
          this.commentsLoading.set(false);
        },
        error: () => {
          // An unreachable thread should not break the popover.
          this.commentThread.set([]);
          this.commentsLoading.set(false);
        },
      });
  }

  /**
   * Sends a comment from the reporter. Requires a stored identity to attribute it, so
   * a first-time commenter is asked for a name and email exactly as when adding
   * feedback; the comment is held until they save.
   */
  onPostComment(newComment: NewComment): void {
    const identity = this.readIdentity();
    if (!identity) {
      this.pendingComment = newComment;
      this.identityModalOpen.set(true);
      return;
    }
    this.postComment(newComment, identity);
  }

  /** Posts a comment on the open feedback item, then refetches the thread. */
  private postComment(newComment: NewComment, identity: ReporterIdentity): void {
    const feedbackId = this.selectedFeedback()?.id;
    if (!feedbackId) {
      return;
    }

    this.commentPosting.set(true);
    this.comments
      .createComment(feedbackId, {
        comment: newComment.comment,
        createdBy: { name: identity.name, email: identity.email },
        // isPrivate is deliberately not sent: the API forces client comments public,
        // and a reporter's own comment has to be visible to them.
      })
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: () => {
          this.commentPosting.set(false);
          this.loadComments(feedbackId);
        },
        error: () => {
          this.commentPosting.set(false);
          this.setStatus('error', 'Could not post the comment. Please try again.');
        },
      });
  }

  /** Maps a stored comment onto the thread component's shape. */
  private toCommentThreadEntry(comment: Comment): CommentThreadEntry {
    return {
      id: comment.id,
      author: comment.createdBy?.name,
      role: comment.createdBy?.role ?? null,
      side: comment.authorType === 'team' ? 'team' : 'client',
      text: comment.comment ?? '',
      created: comment.createdAt,
      isPrivate: comment.isPrivate,
    };
  }

  /** Closes the detail popover and un-reveals/de-highlights its marker. */
  onFeedbackDetailClose(): void {
    if (this.selectedMarker) {
      this.selectedMarker.classList.remove('is-active', 'is-pulsing');
      this.selectedMarker = null;
    }
    this.selectedFeedback.set(null);
    this.detailAnchor.set(null);
    this.commentThread.set([]);
  }

  /** Whether the new-feedback draft popover is open. */
  public readonly newFeedbackOpen = signal(false);

  /** Object URL of the captured screenshot Blob (shown on the draft canvas). */
  public readonly screenshotUrl = signal<string | null>(null);

  /**
   * Warning shown in the draft popover when the page uses features the in-page
   * screenshot may not capture accurately (Elementor / parallax / cross-origin
   * images). Null when the capture looks reliable.
   */
  public readonly captureWarning = signal<string | null>(null);

  /** The reporter chose "Continue without a screenshot": the draft shows no screenshot section. */
  public readonly screenshotSkipped = signal(false);

  /** The captured screenshot Blob, kept for upload when the draft is submitted. */
  private screenshotBlob: Blob | null = null;

  /** The reporter shown in the draft popover (from the stored identity). */
  public readonly reporterIdentity = signal<ReporterIdentity | null>(null);

  /**
   * Hides the whole widget UI for the instant the screenshot frame is grabbed, so
   * the already-open draft popover (and the marker) aren't baked into the shot.
   * Bound to the host's `is-capturing` class (see the component `host` metadata).
   */
  public readonly capturing = signal(false);

  /** Current value of the delayed-capture countdown; null when not counting down. */
  public readonly countdownValue = signal<number | null>(null);

  /** True during the delayed-capture countdown — hides the FAB while it runs. */
  public readonly countingDown = signal(false);

  /** True while a screen recording is in progress (shows the Stop control + count). */
  public readonly recording = signal(false);

  /** Seconds left in the recording (counts down from 60). */
  public readonly videoCountdown = signal(60);

  /** Object URL of the recorded video Blob (played back in the draft popover). */
  public readonly videoUrl = signal<string | null>(null);

  /** The recorded video Blob, kept for upload when the draft is submitted. */
  private videoBlob: Blob | null = null;

  /** Interval driving the recording countdown display. */
  private videoCountdownTimer: ReturnType<typeof setInterval> | null = null;

  /**
   * Entry point for the toolbar's "Add feedback" button. Requires a stored
   * reporter identity first: if one exists in localStorage we go straight to
   * pin placement; otherwise we open the identity modal to collect it.
   */
  onAddFeedback(): void {
    if (this.readIdentity()) {
      this.beginAddFeedback();
      return;
    }
    this.identityModalOpen.set(true);
  }

  /**
   * Stores the identity collected by the modal, then continues whatever needed it: a
   * comment waiting to be sent, or otherwise the add-feedback flow.
   */
  onIdentitySave(identity: ReporterIdentity): void {
    this.writeIdentity(identity);
    this.identityModalOpen.set(false);

    const queuedComment = this.pendingComment;
    if (queuedComment) {
      this.pendingComment = null;
      this.postComment(queuedComment, identity);
      return;
    }

    this.beginAddFeedback();
  }

  /**
   * Reserves a feedback stub on the server (which assigns the marker number for
   * this page) and, on success, enters placement mode with that number.
   */
  private beginAddFeedback(): void {
    const roundId = this.feedbackRoundId;
    if (!roundId) {
      return;
    }
    this.feedback
      .getFeedbackStub({ feedbackRoundId: roundId, pageUrl: window.location.href })
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (stub) => {
          this.pendingStub = stub;
          const markerNumber = Number((stub.marker as { id?: unknown }).id) || 1;
          this.captureMarkerNumber.set(markerNumber);
          this.startPlacing();
        },
        error: () => {
          // Couldn't reserve a stub — surface in the panel and stay put.
          this.setStatus('error', 'Could not start feedback. Please try again.');
        },
      });
  }

  /** Closes the identity modal without saving (Cancel / scrim / Esc). */
  onIdentityCancel(): void {
    this.identityModalOpen.set(false);
    // Drop any comment that was waiting on the identity, rather than sending it later
    // from a flow the reporter abandoned.
    this.pendingComment = null;
  }

  /** Reads and validates the stored identity; null if absent or malformed. */
  private readIdentity(): ReporterIdentity | null {
    try {
      const raw = localStorage.getItem(AppComponent.IDENTITY_STORAGE_KEY);
      if (!raw) {
        return null;
      }
      const parsed = JSON.parse(raw) as { name?: unknown; email?: unknown };
      if (typeof parsed?.name === 'string' && typeof parsed?.email === 'string' && parsed.name && parsed.email) {
        return { name: parsed.name, email: parsed.email };
      }
    } catch {
      // Malformed/blocked storage — treat as no stored identity.
    }
    return null;
  }

  /** Persists the reporter identity for reuse on later feedback. */
  private writeIdentity(identity: ReporterIdentity): void {
    try {
      localStorage.setItem(AppComponent.IDENTITY_STORAGE_KEY, JSON.stringify(identity));
    } catch {
      // Storage may be unavailable (private mode / blocked) — non-fatal.
    }
  }

  /**
   * Enters marker-placement mode: closes the side panel (and hides the FAB via
   * the template guard) so the dimmed page is unobstructed. The crosshair
   * overlay + cursor hint are rendered by <app-placement-overlay>.
   */
  private startPlacing(): void {
    this.panelOpen.set(false);
    this.placing.set(true);
  }

  /** Leaves placement mode without dropping a marker, restoring the panel. */
  cancelPlacing(): void {
    this.placing.set(false);
    this.panelOpen.set(true);
  }

  /**
   * Handles the placement overlay's click: drops a marker at that point, exits
   * placement mode, and opens the capture-timing modal for that marker.
   */
  onPlace(event: MouseEvent): void {
    if (!this.placing()) {
      return;
    }
    this.markerAnchorX.set(event.clientX);
    this.markerAnchorY.set(event.clientY);
    // Record the marker data for the API: absolute page position, the viewport it
    // was placed in, its cell in the 6×24 page grid, and the XPath of the element
    // beneath it. The XPath is resolved here (before the marker is added) so the
    // marker/overlay don't sit on top of the hit-test.
    this.markerPageX = Math.round(event.clientX + window.scrollX);
    this.markerPageY = Math.round(event.clientY + window.scrollY);
    this.markerScreenWidth = window.innerWidth;
    this.markerScreenHeight = window.innerHeight;
    this.markerGrid = this.gridCellFor(this.markerPageX, this.markerPageY);
    this.markerXPath = this.xpathUnderPoint(event.clientX, event.clientY);
    this.dropMarker(event.clientX, event.clientY, this.captureMarkerNumber());
    this.placing.set(false);
    this.captureModalOpen.set(true);
  }

  /**
   * Injects a marker into the host page (the body-level marker layer — light DOM,
   * so the global `.brafton-marker` styles apply). Positioned in page coordinates
   * so it stays anchored to the content when the page scrolls, via MarkerLayer so
   * it lands exactly where it was clicked even when the page's body is offset.
   */
  private dropMarker(clientX: number, clientY: number, markerNumber: number): void {
    const marker = document.createElement('div');
    marker.className = 'brafton-marker is-active';
    marker.textContent = String(markerNumber);
    MarkerLayer.place(marker, clientX + window.scrollX, clientY + window.scrollY);
    this.pendingMarker = marker;
  }

  /** Removes the pending (just-dropped) marker, if any. */
  private removePendingMarker(): void {
    if (this.pendingMarker) {
      this.pendingMarker.remove();
      this.pendingMarker = null;
    }
  }

  /** "Take it now": capture an immediate screenshot. */
  onCaptureNow(): void {
    this.runCapture(0);
  }

  /** "Wait 3 seconds": capture after a 3-second countdown (time to set the page up). */
  onCaptureDelay(): void {
    this.runCapture(3);
  }

  /**
   * "Upload screenshot" (shown when native capture is unavailable): skip html2canvas
   * entirely. The marker is already placed, so just open the draft popover with no
   * auto screenshot and a note prompting the reporter to upload one from their device.
   */
  onCaptureUpload(): void {
    this.captureModalOpen.set(false);
    this.screenshotSkipped.set(false);
    this.screenshotBlob = null;
    this.screenshotUrl.set(null);
    this.videoUrl.set(null);
    this.captureWarning.set(
      `Native screen capture isn't available on this device. Upload a screenshot you took ` +
        `manually with your device to attach it to this feedback.`,
    );
    this.reporterIdentity.set(this.readIdentity());
    this.newFeedbackOpen.set(true);
  }

  /**
   * "Continue without a screenshot": opens the draft beside the marker with nothing
   * captured. The reporter can still attach files with "Add assets".
   */
  onCaptureSkip(): void {
    this.captureModalOpen.set(false);
    this.captureWarning.set(null);
    this.screenshotBlob = null;
    this.screenshotUrl.set(null);
    this.videoUrl.set(null);
    this.videoBlob = null;
    this.screenshotSkipped.set(true);
    this.reporterIdentity.set(this.readIdentity());
    this.newFeedbackOpen.set(true);
  }

  /**
   * Runs a screenshot capture (`delaySeconds` > 0 shows a countdown first) and —
   * only AFTER the Blob is back — opens the new-feedback draft popover beside the
   * marker with the screenshot on its canvas.
   *
   * The capture resolves through browser media callbacks that run outside
   * Angular's zone; setting signals here notifies the reactive graph, so the view
   * recalculates without waiting for a zone-driven change-detection cycle.
   *
   * During the countdown the FAB is hidden (so the user can set up the page) but
   * the count stays visible; only at the moment the frame is grabbed is the whole
   * widget hidden. The dropped marker is deliberately left visible so it lands in
   * the shot. A cancelled/failed capture discards the marker and restores the panel.
   */
  private runCapture(delaySeconds: number): void {
    this.captureModalOpen.set(false);
    this.captureWarning.set(null);
    this.screenshotSkipped.set(false);

    this.screenshotCapture
      .captureScreenshot(delaySeconds)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((event) => {
        switch (event.kind) {
          case 'countdown':
            // Show the count while > 0; clear it at 0, just before the grab.
            this.countingDown.set(event.secondsRemaining > 0);
            this.countdownValue.set(event.secondsRemaining > 0 ? event.secondsRemaining : null);
            break;
          case 'preparing':
            // Hide the whole widget for the grab; the marker stays visible (it
            // lives in the host page's light DOM) so it lands in the screenshot.
            this.countingDown.set(false);
            this.countdownValue.set(null);
            this.capturing.set(true);
            break;
          case 'warning':
            // The page uses features the in-page screenshot may not render
            // accurately (Elementor / parallax / cross-origin images); surface it
            // in the draft popover so the reporter can switch to screen recording.
            this.captureWarning.set(event.message);
            break;
          case 'screenshot':
            this.capturing.set(false);
            this.screenshotBlob = event.blob;
            this.screenshotUrl.set(URL.createObjectURL(event.blob));
            this.reporterIdentity.set(this.readIdentity());
            // Open the draft only after the screenshot has been captured.
            this.newFeedbackOpen.set(true);
            break;
          case 'cancelled':
          case 'error':
            // Capture aborted — reset, drop the pending marker, restore the panel.
            this.capturing.set(false);
            this.countingDown.set(false);
            this.countdownValue.set(null);
            this.removePendingMarker();
            this.panelOpen.set(true);
            break;
        }
      });
  }

  /**
   * "Capture Video": records the screen (up to 60s). When recording begins the
   * widget reveals a Stop control + 60s countdown; on stop/elapse the recorded
   * Blob and its first-frame poster come back and the draft popover opens with
   * the poster on the canvas and the video embedded for review.
   */
  onCaptureVideo(): void {
    this.captureModalOpen.set(false);

    this.screenshotCapture
      .captureVideo()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((event) => {
        switch (event.kind) {
          case 'preparing':
            // Hide the widget chrome before the first-frame poster is grabbed.
            this.capturing.set(true);
            break;
          case 'recording':
            // Recording started — reveal the Stop control + countdown.
            this.capturing.set(false);
            this.recording.set(true);
            this.startVideoCountdown();
            break;
          case 'video':
            this.recording.set(false);
            this.clearVideoCountdown();
            this.capturing.set(false);
            if (event.posterBlob) {
              this.screenshotBlob = event.posterBlob;
              this.screenshotUrl.set(URL.createObjectURL(event.posterBlob));
            }
            this.videoBlob = event.blob;
            this.videoUrl.set(URL.createObjectURL(event.blob));
            this.reporterIdentity.set(this.readIdentity());
            this.newFeedbackOpen.set(true);
            break;
          case 'cancelled':
          case 'error':
            this.recording.set(false);
            this.clearVideoCountdown();
            this.capturing.set(false);
            this.removePendingMarker();
            this.panelOpen.set(true);
            break;
        }
      });
  }

  /** Stops an in-progress recording (Stop button). The `video` event follows. */
  stopVideoCapture(): void {
    this.screenshotCapture.stopVideo();
  }

  /** Starts the 60→0 recording countdown; stops the recording when it hits 0. */
  private startVideoCountdown(): void {
    this.clearVideoCountdown();
    this.videoCountdown.set(60);
    this.videoCountdownTimer = setInterval(() => {
      const remaining = this.videoCountdown() - 1;
      this.videoCountdown.set(remaining);
      if (remaining <= 0) {
        this.clearVideoCountdown();
        this.stopVideoCapture();
      }
    }, 1000);
  }

  /** Clears the recording countdown interval. */
  private clearVideoCountdown(): void {
    if (this.videoCountdownTimer !== null) {
      clearInterval(this.videoCountdownTimer);
      this.videoCountdownTimer = null;
    }
  }

  /** Cancels the capture modal, discards the just-dropped marker, restores panel. */
  onCaptureCancel(): void {
    this.removePendingMarker();
    this.captureModalOpen.set(false);
    this.panelOpen.set(true);
  }

  /** Discards the draft (and its marker), releasing the screenshot URL. */
  onNewFeedbackCancel(): void {
    this.removePendingMarker();
    this.closeNewFeedback();
    this.panelOpen.set(true);
  }

  /**
   * Uploads the current screenshot (canvas state, with annotations) and any
   * recorded video via signed URLs, then finalizes the feedback stub into a real
   * feedback item. A loading bar runs in the popover footer for the whole
   * process. If a video was recorded, "[Video provided]" is appended to the
   * observation and the video URL is added to fileUrls.
   */
  async onNewFeedbackSubmit(draft: NewFeedbackDraft): Promise<void> {
    const roundId = this.feedbackRoundId;
    if (!roundId || this.submitting()) {
      return;
    }
    this.submitting.set(true);
    try {
      const markerNumber = this.captureMarkerNumber();
      // A unique prefix shared by this submission's files so repeated markers
      // (e.g. another "pin-1") can't overwrite an earlier upload in the bucket,
      // while keeping the screenshot and recording grouped under one id.
      const uniqueId = this.generateUniqueId();
      const screenshotUrl = draft.screenshot
        ? await this.uploadBlob(roundId, draft.screenshot, `${uniqueId}-pin-${markerNumber}-screenshot.png`)
        : null;

      let videoUrl: string | null = null;
      if (this.videoBlob) {
        videoUrl = await this.uploadBlob(roundId, this.videoBlob, `${uniqueId}-pin-${markerNumber}-recording.webm`);
      }

      const assetUrls = await this.uploadAssets(roundId, uniqueId, draft.assets);

      const observation = videoUrl ? `[Video provided] ${draft.observation}`.trim() : draft.observation;

      await firstValueFrom(
        this.feedback.createFeedback({
          feedbackRoundId: roundId,
          id: this.pendingStub?.id,
          createdBy: this.reporterIdentity() ?? this.readIdentity(),
          fullUrl: window.location.href,
          // No category: the API picks one with AI after saving.
          element: draft.element || null,
          observation: observation || null,
          screenshotUrl,
          fileUrls: videoUrl || assetUrls.length ? [...(videoUrl ? [videoUrl] : []), ...assetUrls] : null,
          meta: this.buildMeta(),
          marker: this.buildMarker(),
        }),
      );

      this.submitting.set(false);
      // Drop the temporary placement marker; the refresh below re-renders it from
      // the API (positioned via the MarkerPositioner) so we don't double it up.
      this.removePendingMarker();
      this.pendingStub = null;
      this.closeNewFeedback();
      this.panelOpen.set(true);
      // Refresh the list so the new item appears (newest first → first page).
      this.feedbackPage.set(1);
      this.loadAllFeedback();
      if (this.feedbackRoundId) {
        // The new item bumps the round-wide pin total in the header.
        this.loadRoundFeedbackTotal(this.feedbackRoundId);
      }
    } catch {
      // Keep the popover open so the reporter can retry.
      this.submitting.set(false);
      this.setStatus('error', 'Could not submit your feedback. Please try again.');
    }
  }

  /**
   * The draft's extra assets as URLs, in the order they were added: each file is
   * uploaded (in parallel) and each link kept as given. A file is stored as
   * `{uniqueId}-{index}/{its own name}`, so the feedback detail shows its real name
   * while two files with the same name can't overwrite each other.
   */
  private async uploadAssets(roundId: string, uniqueId: string, assets: FeedbackAssetDraft[]): Promise<string[]> {
    const assetUrls = await Promise.all(
      assets.map((asset, assetIndex) =>
        asset.file
          ? this.uploadBlob(roundId, asset.file, `${uniqueId}-${assetIndex + 1}/${this.safeFileName(asset.name)}`)
          : Promise.resolve(asset.url ?? ''),
      ),
    );
    return assetUrls.filter((assetUrl) => assetUrl !== '');
  }

  /** A file name safe as one bucket key segment: no slashes or odd characters. */
  private safeFileName(fileName: string): string {
    const safeName = fileName
      .replace(/[^\w.\- ]+/g, '-')
      .replace(/\s+/g, '-')
      .replace(/^\.+/, '');
    return safeName || 'file';
  }

  /**
   * Requests a signed upload URL for a Blob, PUTs it directly, and resolves to the
   * stored object URL (the signed URL without its query string).
   */
  private async uploadBlob(roundId: string, blob: Blob, filename: string): Promise<string> {
    const contentType = blob.type || 'application/octet-stream';
    const signedUrl = await firstValueFrom(
      this.files.getSignedUrl({ feedbackRoundId: roundId, filename, contentType }),
    );
    await firstValueFrom(this.http.put(signedUrl, blob, { headers: { 'Content-Type': contentType } }));
    return signedUrl.split('?')[0];
  }

  /**
   * Generates a unique id used to prefix uploaded filenames so they never
   * collide in the bucket. Prefers crypto.randomUUID (a v4 UUID); falls back to
   * a timestamp + random suffix on insecure (http) contexts where randomUUID is
   * unavailable.
   */
  private generateUniqueId(): string {
    if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
      return crypto.randomUUID();
    }
    return `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
  }

  /** The environment metadata recorded against the feedback item. */
  private buildMeta(): Record<string, unknown> {
    // detect-browser parses the user agent into a browser name + version (and OS).
    const browserInfo = detect();
    return {
      screen: this.screenType(),
      // The viewport size the feedback was given against.
      resolution: `${window.innerWidth}x${window.innerHeight}`,
      browser: browserInfo?.name ?? 'unknown',
      // Just the browser version, recorded as the app version.
      appVersion: browserInfo?.version ?? 'unknown',
      // The widget build that submitted this feedback.
      widgetVersion: WIDGET_VERSION,
      os: browserInfo?.os ?? navigator.platform,
      orientation:
        typeof window.matchMedia === 'function' && window.matchMedia('(orientation: portrait)').matches
          ? 'portrait'
          : 'landscape',
      pageTitle: document.title,
      description: this.pageDescription(),
    };
  }

  /** The page's meta description (empty when there isn't one). */
  private pageDescription(): string {
    const meta = document.querySelector('meta[name="description"]');
    return meta?.getAttribute('content')?.trim() ?? '';
  }

  /**
   * The marker record: absolute page position, the viewport it was placed in, its
   * cell in the 6×24 page grid, and the XPath of the element directly beneath it.
   */
  private buildMarker(): Record<string, unknown> {
    return {
      id: this.captureMarkerNumber(),
      x: this.markerPageX,
      y: this.markerPageY,
      screen: [this.markerScreenWidth, this.markerScreenHeight],
      element: this.markerXPath,
      grid: this.markerGrid,
    };
  }

  /**
   * The 1-based grid cell for a page coordinate, dividing the whole page into a
   * fixed 6 (columns) × 24 (rows) grid. Numbered left-to-right, top-to-bottom.
   */
  private gridCellFor(pageX: number, pageY: number): number {
    const columns = 6;
    const rows = 24;
    const pageWidth = Math.max(document.documentElement.scrollWidth, 1);
    const pageHeight = Math.max(document.documentElement.scrollHeight, 1);
    const column = Math.min(columns - 1, Math.max(0, Math.floor((pageX / pageWidth) * columns)));
    const row = Math.min(rows - 1, Math.max(0, Math.floor((pageY / pageHeight) * rows)));
    return row * columns + column + 1;
  }

  /**
   * Resolves the XPath of the topmost page element at a viewport point, skipping
   * the widget's own chrome (the host element and any dropped markers).
   */
  private xpathUnderPoint(clientX: number, clientY: number): string {
    const stack = document.elementsFromPoint(clientX, clientY);
    const target = stack.find(
      (element) =>
        element.tagName.toLowerCase() !== 'brafton-feedback' &&
        !element.classList.contains('brafton-marker'),
    );
    return target ? this.xpathFor(target) : '';
  }

  /** Builds an absolute XPath (e.g. /html[1]/body[1]/div[2]) for an element. */
  private xpathFor(element: Element): string {
    const segments: string[] = [];
    let node: Element | null = element;
    while (node && node.nodeType === Node.ELEMENT_NODE) {
      let index = 1;
      let sibling = node.previousElementSibling;
      while (sibling) {
        if (sibling.tagName === node.tagName) {
          index++;
        }
        sibling = sibling.previousElementSibling;
      }
      segments.unshift(`${node.tagName.toLowerCase()}[${index}]`);
      if (node.tagName.toLowerCase() === 'html') {
        break;
      }
      node = node.parentElement;
    }
    return `/${segments.join('/')}`;
  }

  /** Standardised screen type from the current viewport width (mirrors the backend). */
  private screenType(): string {
    const width = window.innerWidth;
    if (width < 768) {
      return 'mobile';
    }
    if (width < 1024) {
      return 'tablet';
    }
    return 'desktop';
  }

  /** Closes the draft popover and releases the screenshot + video object URLs. */
  private closeNewFeedback(): void {
    this.newFeedbackOpen.set(false);
    this.captureWarning.set(null);
    const screenshot = this.screenshotUrl();
    if (screenshot) {
      URL.revokeObjectURL(screenshot);
    }
    this.screenshotUrl.set(null);
    this.screenshotBlob = null;
    const video = this.videoUrl();
    if (video) {
      URL.revokeObjectURL(video);
    }
    this.videoUrl.set(null);
    this.videoBlob = null;
  }

  /** Esc backs out of placement, the capture modal, the draft, then identity. */
  @HostListener('document:keydown.escape')
  onEscape(): void {
    if (this.mockupOpen()) {
      this.closeMockup();
    } else if (this.placing()) {
      this.cancelPlacing();
    } else if (this.captureModalOpen()) {
      this.onCaptureCancel();
    } else if (this.newFeedbackOpen()) {
      this.onNewFeedbackCancel();
    } else if (this.identityModalOpen()) {
      this.onIdentityCancel();
    } else if (this.selectedFeedback()) {
      this.onFeedbackDetailClose();
    }
  }

  /** Outcome of the feedback-round validation, shown in the panel. */
  public statusMessage: string | null = null;
  public statusKind: StatusKind | null = null;

  /** True when the host element has no `org`/`project` — shows a setup prompt. */
  public missingConfig = false;

  ngOnInit(): void {
    this.installTempAuthControls();

    // Cancel any pending Sign-in re-probe if the widget is torn down first.
    this.destroyRef.onDestroy(() => {
      if (this.signInRetryTimer !== null) {
        clearTimeout(this.signInRetryTimer);
      }
    });

    const element = this.host.nativeElement as HTMLElement;
    const orgId = element.getAttribute('org')?.trim() ?? '';
    const projectId = element.getAttribute('project')?.trim() ?? '';

    // No org/project → show the setup prompt and do NOT validate the round or
    // run the auth bridge.
    if (!orgId || !projectId) {
      this.missingConfig = true;
      return;
    }

    // On non-trusted hosts (reached only via ?brafton-feedback=true) the widget
    // must be confirmed against the project's allowed-domain list before it may
    // appear. Hide it up front so the launcher never flashes before we know the
    // domain is authorized; trusted hosts (localhost / brafton subdomains) skip
    // the check and stay visible.
    const embedCheckRequired = requiresEmbedAllowedCheck(window.location);
    if (embedCheckRequired) {
      this.hideWidgetHost();
    }

    this.feedbackRounds.validateFeedbackRound(orgId, projectId).subscribe({
      next: (response) => {
        if (!response.valid) {
          // Reveal in case we hid the widget above, so the round-error UI shows.
          this.revealWidgetHost();
          this.setStatus('error', response.message ?? 'This feedback round is not available.');
          return;
        }
        if (embedCheckRequired) {
          this.enforceEmbedAllowed(response);
          return;
        }
        this.activateValidatedRound(response);
      },
      error: (error) => {
        this.revealWidgetHost();
        this.setStatus('error', this.errorMessage(error));
      },
    });
  }

  /**
   * Verifies the current hostname against the round's allowed-domain list before
   * activating. Fails closed: a `false` result OR an errored check hides the
   * widget and logs that the domain isn't allowed. The validate JWT is passed
   * explicitly because the token isn't stored until {@link activateValidatedRound}.
   */
  private enforceEmbedAllowed(response: ValidateRoundResponse): void {
    const roundId = response.feedbackRoundId;
    if (!roundId) {
      this.denyEmbedAccess();
      return;
    }
    this.feedbackRounds
      .checkEmbedAllowed(roundId, window.location.hostname, response.token ?? undefined)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (allowed) => {
          if (allowed) {
            this.revealWidgetHost();
            this.activateValidatedRound(response);
          } else {
            this.denyEmbedAccess();
          }
        },
        error: () => this.denyEmbedAccess(),
      });
  }

  /**
   * Finishes initialization for a validated round: stores the JWT, loads this
   * page's feedback/mockup/status, and probes the auth bridge for a logged-in
   * session.
   */
  private activateValidatedRound(response: ValidateRoundResponse): void {
    if (response.token) {
      this.tokenStore.set(response.token);
    }
    this.feedbackRoundId = response.feedbackRoundId ?? null;
    this.setStatus('success', response.message ?? 'Feedback round validated. You can now leave feedback.');
    // Load the feedback already recorded for this page.
    this.loadAllFeedback();
    if (response.feedbackRoundId) {
      // Resolve this page's mockup + status, and the round-wide pin total.
      this.loadCurrentPage(response.feedbackRoundId);
      this.loadRoundFeedbackTotal(response.feedbackRoundId);
    }
    if (response.token && response.feedbackRoundId) {
      this.bridgeJwt = response.token;
      this.detectLogin(response.token, response.feedbackRoundId);
    }
  }

  /** Hides the whole widget (host element) without removing it from the DOM. */
  private hideWidgetHost(): void {
    (this.host.nativeElement as HTMLElement).style.display = 'none';
  }

  /** Reverses {@link hideWidgetHost}. */
  private revealWidgetHost(): void {
    (this.host.nativeElement as HTMLElement).style.display = '';
  }

  /**
   * Gracefully blocks a disallowed domain: removes the widget from the page and
   * logs a clearly formatted notice naming the offending host.
   */
  private denyEmbedAccess(): void {
    (this.host.nativeElement as HTMLElement).remove();
    const domain = window.location.hostname;
    console.log(
      `%c Brafton Feedback %c Domain not allowed \n\n%c${domain}%c is not on this project's list of allowed domains, so the feedback widget was not loaded.\nAdd this domain to the project's allowed domains to enable feedback here.`,
      'background:#111827;color:#ffffff;font-weight:700;padding:2px 6px;border-radius:4px 0 0 4px;',
      'background:#e11d48;color:#ffffff;font-weight:700;padding:2px 6px;border-radius:0 4px 4px 0;',
      'color:#e11d48;font-weight:700;',
      'color:inherit;font-weight:400;',
    );
  }

  /**
   * Ask the auth bridge whether this browser is a logged-in Brafton user
   * authorized for the round. Drives the Sign in vs AI button in the header.
   */
  private detectLogin(jwt: string, feedbackRoundId: string): void {
    this.authBridge
      .detectBraftonSession(this.appBaseUrl, jwt, feedbackRoundId)
      .subscribe((result) => this.loggedIn.set(result.loggedIn));
  }

  /**
   * The Sign-in link opens the Feedback app in a new tab where the user logs in.
   * We can't observe that tab, so 60s later we re-probe the auth bridge — by then
   * the Brafton session cookie is usually set, flipping the header to the logged-in
   * state without the user reloading the host page. Repeated clicks reset the timer.
   */
  protected onSignIn(): void {
    const jwt = this.bridgeJwt;
    const roundId = this.feedbackRoundId;
    if (!jwt || !roundId) {
      return;
    }
    if (this.signInRetryTimer !== null) {
      clearTimeout(this.signInRetryTimer);
    }
    this.signInRetryTimer = setTimeout(() => {
      this.signInRetryTimer = null;
      this.detectLogin(jwt, roundId);
    }, this.signInRetryMs);
  }

  // ---------------------------------------------------------------------------
  // TEMPORARY: simulate the auth-bridge login state from the browser console
  // (`temp_login()` / `temp_logout()`) while the real bridge can't run over the
  // local http/https split. REMOVE this whole block once the bridge works locally.
  // ---------------------------------------------------------------------------
  private installTempAuthControls(): void {
    const globalScope = window as unknown as {
      temp_login?: () => void;
      temp_logout?: () => void;
    };
    globalScope.temp_login = () => this.loggedIn.set(true);
    globalScope.temp_logout = () => this.loggedIn.set(false);
    console.info('[brafton-feedback] TEMP auth controls ready — run temp_login() / temp_logout() in the console.');
  }

  openPanel(): void {
    this.panelOpen.set(true);
  }

  closePanel(): void {
    this.panelOpen.set(false);
  }

  private setStatus(kind: StatusKind, message: string): void {
    this.statusKind = kind;
    this.statusMessage = message;
  }

  /** Prefer the API's error message; fall back to a generic failure. */
  private errorMessage(error: unknown): string {
    const apiMessage = (error as { error?: { message?: string } })?.error?.message;
    return apiMessage ?? 'We could not validate this feedback round. Please try again later.';
  }
}
